import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2 } from 'lucide-react';
import { useCreateOrder, useUpdateOrder } from '../../hooks/useOrders.js';
import { useBuckets } from '../../hooks/useBuckets.js';
import { useStages } from '../../hooks/useStages.js';
import { useFieldDefinitions } from '../../hooks/useFieldDefinitions.js';
import { useValidationRules } from '../../hooks/useValidationRules.js';
import { runValidationRules } from '../../lib/validationEngine.js';
import { toast } from '../../lib/toast.js';
import { useWorkspaceStore } from '../../stores/workspace.js';
import CustomFieldInput from '../customFields/CustomFieldInput.js';
import type { Order } from '@mayksel/types';

interface Props {
  order?: Order | null;
  defaultBucketId?: string | undefined;
  defaultBucketName?: string | undefined;
  onClose: () => void;
}

interface LineItemRow {
  id?: string;
  description: string;
  quantity: string;
  unitPrice: string;
  press: string;
}

interface FormState {
  bucketId: string;
  bucketName: string;
  name: string;
  status: string;
  stageId: string;
  priority: string;
  dueDate: string;
  tax: string;
  notes: string;
  lineItems: LineItemRow[];
}

const STATUS_OPTIONS = ['draft', 'in_production', 'complete', 'cancelled'] as const;
const PRIORITY_OPTIONS = ['low', 'normal', 'high', 'urgent'] as const;

function calcSubtotal(items: LineItemRow[]): number {
  return items.reduce((sum, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unitPrice) || 0;
    return sum + qty * price;
  }, 0);
}

export default function OrderModal({ order, defaultBucketId, defaultBucketName, onClose }: Props) {
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('common');
  const workspace = useWorkspaceStore((s) => s.workspace);
  const overlayRef = useRef<HTMLDivElement>(null);

  const [bucketSearch, setBucketSearch] = useState('');
  const [showBucketDropdown, setShowBucketDropdown] = useState(false);

  const createOrder = useCreateOrder();
  const updateOrder = useUpdateOrder(order?.id ?? '');
  const { data: stagesData } = useStages();
  const { data: customFieldDefs = [] } = useFieldDefinitions('order');
  const { data: validationRulesList = [] } = useValidationRules('order');
  const activeCustomFields = customFieldDefs.filter((f) => f.isActive);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>(
    order?.customFields ?? {},
  );

  const { data: bucketsData } = useBuckets({
    ...(bucketSearch ? { search: bucketSearch } : {}),
    limit: 20,
  });

  const [form, setForm] = useState<FormState>(() => ({
    bucketId: order?.bucketId ?? defaultBucketId ?? '',
    bucketName: defaultBucketName ?? '',
    name: order?.name ?? '',
    status: order?.status ?? 'draft',
    stageId: order?.stageId ?? '',
    priority: order?.priority ?? 'normal',
    dueDate: order?.dueDate ?? '',
    tax: order?.tax ?? '0',
    notes: order?.notes ?? '',
    lineItems: order?.lineItems?.map((li) => ({
      id: li.id,
      description: li.description,
      quantity: li.quantity,
      unitPrice: li.unitPrice,
      press: li.press ?? '',
    })) ?? [],
  }));

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  function selectBucket(id: string, name: string) {
    setForm((prev) => ({ ...prev, bucketId: id, bucketName: name }));
    setBucketSearch('');
    setShowBucketDropdown(false);
  }

  function addLineItem() {
    setForm((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', quantity: '1', unitPrice: '0', press: '' }],
    }));
  }

  function removeLineItem(idx: number) {
    setForm((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== idx),
    }));
  }

  function updateLineItem(idx: number, field: keyof LineItemRow, value: string) {
    setForm((prev) => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) =>
        i === idx ? { ...item, [field]: value } : item,
      ),
    }));
  }

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!form.bucketId) newErrors.bucket = `${orderLabel} is required`;
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (form.tax && !/^\d+(\.\d{1,2})?$/.test(form.tax)) newErrors.tax = 'Invalid tax amount';
    for (const item of form.lineItems) {
      if (!item.description.trim()) { newErrors.lineItems = 'Each line item needs a description'; break; }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    const isStageChange = !!order && form.stageId !== (order.stageId ?? '');
    const entityData: Record<string, unknown> = {
      name: form.name.trim(),
      status: form.status,
      stageId: form.stageId,
      priority: form.priority,
      dueDate: form.dueDate,
      tax: form.tax,
      notes: form.notes.trim(),
      ...customFieldValues,
    };
    const triggers: ('on_create' | 'on_update' | 'on_stage_change')[] = order
      ? ['on_update', ...(isStageChange ? ['on_stage_change' as const] : [])]
      : ['on_create'];
    const violations = triggers.flatMap((trigger) =>
      runValidationRules(validationRulesList, entityData, trigger),
    );
    const seen = new Set<string>();
    const uniqueViolations = violations.filter((v) => {
      if (seen.has(v.ruleId)) return false;
      seen.add(v.ruleId);
      return true;
    });
    const blocking = uniqueViolations.filter((v) => v.actionType !== 'show_warning');
    if (blocking.length > 0) {
      blocking.forEach((v) => toast.error(v.message));
      return;
    }
    uniqueViolations.filter((v) => v.actionType === 'show_warning').forEach((v) => toast.error(v.message));

    const lineItems = form.lineItems
      .filter((item) => item.description.trim())
      .map((item) => {
        const row: {
          description: string;
          quantity: string;
          unitPrice: string;
          customFields: Record<string, unknown>;
          id?: string;
          press?: string;
        } = {
          description: item.description.trim(),
          quantity: item.quantity || '1',
          unitPrice: item.unitPrice || '0',
          customFields: {},
        };
        if (item.id) row.id = item.id;
        if (item.press.trim()) row.press = item.press.trim();
        return row;
      });

    const base: {
      name: string;
      status: 'draft' | 'in_production' | 'complete' | 'cancelled';
      priority: 'low' | 'normal' | 'high' | 'urgent';
      isWhiteLabel: boolean;
      tax: string;
      customFields: Record<string, unknown>;
      lineItems: typeof lineItems;
      notes?: string;
      stageId?: string;
      dueDate?: string;
    } = {
      name: form.name.trim(),
      status: form.status as 'draft' | 'in_production' | 'complete' | 'cancelled',
      priority: form.priority as 'low' | 'normal' | 'high' | 'urgent',
      isWhiteLabel: false,
      tax: form.tax || '0',
      customFields: customFieldValues,
      lineItems,
    };
    if (form.notes.trim()) base.notes = form.notes.trim();
    if (form.stageId) base.stageId = form.stageId;
    if (form.dueDate) base.dueDate = form.dueDate;

    try {
      if (order) {
        await updateOrder.mutateAsync(base);
        toast.success(t('toast.updated', { label: orderLabel }));
      } else {
        await createOrder.mutateAsync({ ...base, bucketId: form.bucketId });
        toast.success(t('toast.created', { label: orderLabel }));
      }
      onClose();
    } catch (_err) {
      toast.error(order
        ? t('toast.update_error', { label: orderLabel })
        : t('toast.create_error', { label: orderLabel }),
      );
    }
  }

  const isPending = createOrder.isPending || updateOrder.isPending;
  const stages = stagesData ?? [];
  const availableBuckets = (bucketsData?.data ?? []);
  const subtotal = calcSubtotal(form.lineItems);
  const tax = parseFloat(form.tax) || 0;
  const orderTotal = subtotal + tax;

  const orderLabel = workspace?.orderLabel ?? 'Order';
  const bucketLabel = workspace?.bucketLabel ?? 'Project';

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={order ? t('form.edit_title', { label: orderLabel }) : t('form.create_title', { label: orderLabel })}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 'var(--z-modal)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 'var(--space-4)',
      }}
    >
      <div style={{
        background: 'var(--color-bg)', borderRadius: 'var(--radius-xl)',
        width: '100%', maxWidth: 640, maxHeight: '90dvh', overflow: 'auto',
        boxShadow: 'var(--shadow-xl)', animation: 'page-enter 200ms var(--ease-out)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--space-5) var(--space-6)', borderBottom: '1px solid var(--color-border)',
          position: 'sticky', top: 0, background: 'var(--color-bg)', zIndex: 1,
        }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
            {order
              ? t('form.edit_title', { label: orderLabel })
              : t('form.create_title', { label: orderLabel })}
          </h2>
          <button onClick={onClose} aria-label={tc('actions.close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', display: 'flex' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>

            {/* Bucket picker (only when creating) */}
            {!order && (
              <Field label={t('form.bucket_label', { bucket_label: bucketLabel })} error={errors.bucket} required>
                {form.bucketId ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: 'var(--space-2) var(--space-3)',
                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--text-sm)',
                  }}>
                    <span style={{ color: 'var(--color-text)' }}>{form.bucketName}</span>
                    {!defaultBucketId && (
                      <button type="button"
                        onClick={() => setForm((prev) => ({ ...prev, bucketId: '', bucketName: '' }))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', display: 'flex' }}>
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text" value={bucketSearch} autoFocus
                      onChange={(e) => { setBucketSearch(e.target.value); setShowBucketDropdown(true); }}
                      onFocus={() => setShowBucketDropdown(true)}
                      onBlur={() => setTimeout(() => setShowBucketDropdown(false), 150)}
                      placeholder={t('form.bucket_placeholder', { bucket_label: bucketLabel })}
                      style={inputStyle(!!errors.bucket)}
                    />
                    {showBucketDropdown && availableBuckets.length > 0 && (
                      <div style={dropdownStyle}>
                        {availableBuckets.map((b) => (
                          <button key={b.id} type="button"
                            onMouseDown={() => selectBucket(b.id, b.name)}
                            style={dropdownItemStyle}
                            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-subtle)')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}>
                            <span style={{ fontWeight: 'var(--font-medium)' }}>{b.name}</span>
                            {b.companyName && (
                              <span style={{ marginLeft: 6, color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
                                · {b.companyName}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Field>
            )}

            {/* Name */}
            <Field label={t('form.name_label')} error={errors.name} required>
              <input type="text" value={form.name} autoFocus={!!order || !!defaultBucketId}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t('form.name_placeholder')} style={inputStyle(!!errors.name)} />
            </Field>

            {/* Status + Priority row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <Field label={t('form.status_label')}>
                <select value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  style={{ ...inputStyle(false), appearance: 'auto' }}>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{t(`status.${s}`)}</option>
                  ))}
                </select>
              </Field>
              <Field label={t('form.priority_label')}>
                <select value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  style={{ ...inputStyle(false), appearance: 'auto' }}>
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p} value={p}>{t(`priority.${p}`)}</option>
                  ))}
                </select>
              </Field>
            </div>

            {/* Stage + Due date row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <Field label={t('form.stage_label')}>
                <select value={form.stageId}
                  onChange={(e) => setForm({ ...form, stageId: e.target.value })}
                  style={{ ...inputStyle(false), appearance: 'auto' }}>
                  <option value="">{t('form.stage_placeholder')}</option>
                  {stages.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </Field>
              <Field label={t('form.due_date_label')}>
                <input type="date" value={form.dueDate}
                  onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                  style={inputStyle(false)} />
              </Field>
            </div>

            {/* Line Items */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                <label style={labelStyle}>{t('form.line_items_label')}</label>
                <button type="button" onClick={addLineItem} style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
                  background: 'none', border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)', padding: '2px var(--space-2)',
                  fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', cursor: 'pointer',
                }}>
                  <Plus size={12} /> {t('form.add_line_item')}
                </button>
              </div>
              {errors.lineItems && (
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-danger)', marginBottom: 'var(--space-2)' }}>
                  {errors.lineItems}
                </p>
              )}

              {form.lineItems.length > 0 && (
                <div style={{
                  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden',
                }}>
                  {/* Column headers */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '2fr 70px 90px 80px 32px',
                    gap: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)',
                    background: 'var(--color-bg-subtle)', borderBottom: '1px solid var(--color-border)',
                  }}>
                    {['Description', 'Qty', 'Unit Price', 'Press', ''].map((h) => (
                      <span key={h} style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-medium)' }}>
                        {h}
                      </span>
                    ))}
                  </div>

                  {form.lineItems.map((item, idx) => (
                    <div key={idx} style={{
                      display: 'grid', gridTemplateColumns: '2fr 70px 90px 80px 32px',
                      gap: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)',
                      borderBottom: idx < form.lineItems.length - 1 ? '1px solid var(--color-gray-100)' : 'none',
                      alignItems: 'center',
                    }}>
                      <input
                        type="text" value={item.description}
                        onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                        placeholder="Item description"
                        style={{ ...inlineInputStyle, flex: 1 }}
                      />
                      <input
                        type="text" value={item.quantity}
                        onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                        style={inlineInputStyle}
                      />
                      <input
                        type="text" value={item.unitPrice}
                        onChange={(e) => updateLineItem(idx, 'unitPrice', e.target.value)}
                        placeholder="0.00"
                        style={inlineInputStyle}
                      />
                      <input
                        type="text" value={item.press}
                        onChange={(e) => updateLineItem(idx, 'press', e.target.value)}
                        placeholder="e.g. 6K"
                        style={inlineInputStyle}
                      />
                      <button type="button" onClick={() => removeLineItem(idx)}
                        aria-label="Remove line item"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', display: 'flex', padding: 0 }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Totals */}
              {(form.lineItems.length > 0 || form.tax !== '0') && (
                <div style={{
                  marginTop: 'var(--space-3)', display: 'flex', flexDirection: 'column',
                  alignItems: 'flex-end', gap: 'var(--space-1)',
                }}>
                  {form.lineItems.length > 0 && (
                    <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{t('form.subtotal_label')}</span>
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', minWidth: 80, textAlign: 'right' }}>
                        {subtotal.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{t('form.tax_row_label')}</span>
                    <input
                      type="text" value={form.tax}
                      onChange={(e) => setForm({ ...form, tax: e.target.value })}
                      style={{ ...inlineInputStyle, minWidth: 80, textAlign: 'right' }}
                    />
                  </div>
                  <div style={{
                    display: 'flex', gap: 'var(--space-4)',
                    paddingTop: 'var(--space-2)', borderTop: '1px solid var(--color-border)',
                  }}>
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                      {t('form.order_total_label')}
                    </span>
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', minWidth: 80, textAlign: 'right' }}>
                      {orderTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <Field label={t('form.notes_label')}>
              <textarea value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3} placeholder={t('form.notes_placeholder')}
                style={{ ...inputStyle(false), resize: 'vertical', fontFamily: 'var(--font-sans)' }} />
            </Field>
          </div>

          {/* Custom Fields */}
          {activeCustomFields.length > 0 && (
            <div style={{ padding: '0 var(--space-6) var(--space-4)' }}>
              <p className="label" style={{ marginBottom: 'var(--space-3)' }}>
                {t('section_custom_fields', { ns: 'customFields' })}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {activeCustomFields.map((field) => (
                  <Field key={field.id} label={field.label}>
                    <CustomFieldInput
                      field={field}
                      value={customFieldValues[field.key]}
                      onChange={(val) => setCustomFieldValues((prev) => ({ ...prev, [field.key]: val }))}
                    />
                  </Field>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{
            display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end',
            padding: 'var(--space-4) var(--space-6)', borderTop: '1px solid var(--color-border)',
            position: 'sticky', bottom: 0, background: 'var(--color-bg)',
          }}>
            <button type="button" onClick={onClose} disabled={isPending} style={cancelBtnStyle}>
              {tc('actions.cancel')}
            </button>
            <button type="submit" disabled={isPending} style={{
              ...saveBtnStyle,
              cursor: isPending ? 'not-allowed' : 'pointer',
              opacity: isPending ? 0.7 : 1,
            }}>
              {isPending ? tc('actions.loading') : tc('actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, error, required }: {
  label: string; children: React.ReactNode; error?: string | undefined; required?: boolean | undefined;
}) {
  return (
    <div>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: 'var(--color-danger)', marginLeft: 2 }}>*</span>}
      </label>
      <div style={{ marginTop: 'var(--space-1)' }}>{children}</div>
      {error && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-danger)', marginTop: 4 }}>{error}</p>}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)',
  color: 'var(--color-text)', display: 'block',
};

function inputStyle(hasError: boolean): React.CSSProperties {
  return {
    width: '100%', padding: 'var(--space-2) var(--space-3)',
    border: hasError ? '1px solid var(--color-danger)' : '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
    color: 'var(--color-text)', background: 'var(--color-bg)', boxSizing: 'border-box',
  };
}

const inlineInputStyle: React.CSSProperties = {
  width: '100%', padding: '4px var(--space-2)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)',
  color: 'var(--color-text)', background: 'var(--color-bg)', boxSizing: 'border-box',
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute', top: '100%', left: 0, right: 0,
  background: 'var(--color-bg)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
  zIndex: 'var(--z-dropdown)', maxHeight: 200, overflow: 'auto', marginTop: 2,
};

const dropdownItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'baseline', width: '100%', textAlign: 'left',
  padding: 'var(--space-2) var(--space-3)',
  fontSize: 'var(--text-sm)', color: 'var(--color-text)',
  background: 'none', border: 'none', cursor: 'pointer',
};

const cancelBtnStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-5)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
  color: 'var(--color-text-secondary)', background: 'var(--color-bg)', cursor: 'pointer',
};

const saveBtnStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-5)', background: 'var(--color-brand)',
  color: 'var(--color-text-inverse)', border: 'none',
  borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-medium)',
};
